import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function luminance(hex) {
  const c = [1, 3, 5].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function applyTheme(colors) {
  if (!colors?.primary) return;
  const root = document.documentElement.style;
  const fg = luminance(colors.primary) > 0.45 ? "#0A0A0A" : "#FFFFFF";
  root.setProperty("--brand", colors.primary);
  root.setProperty("--brand-fg", fg);
  root.setProperty("--brand-accent", colors.accent || colors.primary);
  root.setProperty("--sidebar", colors.sidebar || "#0A0A0A");
  root.setProperty("--primary", hexToHsl(colors.primary));
  root.setProperty("--ring", hexToHsl(colors.primary));
  root.setProperty("--primary-foreground", fg === "#FFFFFF" ? "0 0% 100%" : "0 0% 4%");
}

export const NOLIUM = { primary: "#11A9A2", secondary: "#083B66", accent: "#A6CE39", sidebar: "#083B66" };

export function AuthProvider({ children }) {
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const platform = data.user.role === "superadmin" && !localStorage.getItem("company_id");
      setState({ ...data, platform });
      applyTheme(platform ? NOLIUM : data.company?.colors);
    } catch {
      setState(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* sessione già chiusa */ }
    localStorage.removeItem("company_id");
    setState(false);
  };

  const can = (module, action) => {
    if (!state?.user) return false;
    if (action === "export" || action === "import") return state.user.role === "superadmin";
    if (["superadmin", "admin"].includes(state.user.role)) return true;
    return (state.user.permissions?.[module] || []).includes(action);
  };

  return <AuthCtx.Provider value={{ state, refresh, logout, can, setState }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
